import type { CSSProperties } from "react";
import type { TextElementProps } from "./types";
import {
  resolveDynamicColor,
  mapTextStyle,
  calculateAutoFontSize,
  getLineClampStyles,
  getVerticalAlignStyles,
} from "./utils";

export interface TextElementComponentProps extends TextElementProps {
  /** Additional CSS class name */
  className?: string;
  /** Container style overrides */
  containerStyle?: CSSProperties;
  /** Whether we're in admin edit mode (allows custom colors) vs render mode (uses brand colors) */
  isAdminMode?: boolean;
}

/**
 * TextElement - Unified text rendering component for all text-based elements.
 * Handles: userName, reviewMessage, placeName, placeAddress, reviewDate, custom text.
 * 
 * CRITICAL: This component ensures consistent text color resolution.
 * - Review text ALWAYS uses primaryText color
 * - Other text uses resolved colors from style or brandColors
 * - Custom empty elements can use accent as background (for decorative shapes)
 */
export function TextElement({
  content,
  elementType,
  isReviewText = false,
  brandColors,
  templateDefaults,
  style,
  opacity,
  borderRadius,
  borderWidth,
  borderColor,
  shadow,
  backgroundBlendMode,
  className,
  containerStyle,
  isAdminMode = false,
}: TextElementComponentProps) {
  // Calculate effective font size with auto-reduction
  const baseFontSize = style?.fontSize ?? 16;
  const effectiveFontSize = calculateAutoFontSize(
    content,
    baseFontSize,
    style?.minFontSize,
    style?.autoReduceFontSize ?? false
  );
  
  // Get base text styles with dynamic color resolution
  let textStyle = mapTextStyle(style, brandColors, templateDefaults, {
    fontSize: `${effectiveFontSize}px`,
  });
  
  // CRITICAL: Only apply brand color fallback if no explicit color is set
  // This allows admin users to customize text colors while maintaining
  // automatic color resolution for templates without explicit colors
  if (isReviewText || elementType === "reviewMessage") {
    // If there's no explicit color in the style, use primaryText as default
    if (!style?.color) {
      textStyle = {
        ...textStyle,
        color: brandColors.primaryText,
      };
    }
  }
  
  // Apply line clamp if specified
  const lineClampStyles = getLineClampStyles(style?.maxLines);
  if (Object.keys(lineClampStyles).length > 0) {
    textStyle = { ...textStyle, ...lineClampStyles };
  }
  
  // Apply vertical alignment
  const verticalAlignStyles = getVerticalAlignStyles(style?.verticalAlign);
  if (Object.keys(verticalAlignStyles).length > 0) {
    textStyle = { ...textStyle, ...verticalAlignStyles };
  }
  
  // Handle custom empty elements - behavior differs between admin and render mode
  // In admin mode: respect explicit colors for customization
  // In render mode: always use current accent for decorative shapes
  let resolvedBackgroundColor = textStyle.backgroundColor;
  if (elementType === "custom" && (!content || content.trim().length === 0)) {
    if (isAdminMode) {
      // Admin mode: keep the resolved color from mapTextStyle (allows custom colors)
      resolvedBackgroundColor = textStyle.backgroundColor;
    } else {
      // Render mode (workspace/preview): always use current accent for empty custom elements
      resolvedBackgroundColor = brandColors.accent;
    }
  }
  
  // Apply container-level styles
  const containerCss: CSSProperties = {
    width: "100%",
    height: "100%",
    ...textStyle,
    backgroundColor: resolvedBackgroundColor,
    opacity: opacity ?? style?.opacity ?? 1,
    mixBlendMode: (backgroundBlendMode ?? style?.backgroundBlendMode) as CSSProperties["mixBlendMode"] | undefined,
    ...containerStyle,
  };
  
  return (
    <div className={className} style={containerCss}>
      {content}
    </div>
  );
}
